import { Cell, CellChange, Row } from "@silevis/reactgrid";
import { ROW_HEIGHT_PX } from "../consts";
import { SpreadsheetRowData } from "@flexidao/ui-lib";

export const mapRowsToSpreadsheetRows = <T extends SpreadsheetRowData>({
    columns,
    rows,
    editable,
}: {
    columns: Array<Extract<keyof T, string>>;
    rows: Array<T>;
    editable: boolean;
}): Array<Row<Cell>> => {
    const headerRow: Row<Cell> = {
        rowId: "header",
        cells: columns.map((columnId) => ({
            type: "header",
            text: columnId,
            nonEditable: true,
        })),
        reorderable: true,
        height: ROW_HEIGHT_PX,
    };

    return [
        headerRow,
        ...rows.map((row): Row<Cell> => {
            const cells: Array<Cell> = columns.map((columnId) => ({
                type: "text",
                text:
                    row[columnId] === undefined || row[columnId] === null
                        ? ""
                        : String(row[columnId]),
                nonEditable: !editable,
            }));
            return {
                rowId: row.rowId as string,
                cells,
                reorderable: true,
                height: ROW_HEIGHT_PX,
            };
        }),
    ];
};

export const applyChangesToRows = <T extends SpreadsheetRowData>({
    changes,
    rows,
}: {
    changes: Array<CellChange>;
    rows: Array<T>;
}): Array<T> => {
    changes.forEach((change) => {
        const rowIndex = rows.findIndex((row) => row.rowId === change.rowId);

        if (rowIndex < 0 || rowIndex >= rows.length) {
            throw new Error(`Row with id ${change.rowId} not found.`);
        }

        const columnId = change.columnId;

        (rows[rowIndex] as any)[columnId] = getStringValueFromCellChange(change);
    });

    return [...rows];
};

const getStringValueFromCellChange = (change: CellChange): string => {
    switch (change.type) {
        case "number":
            return change.newCell.value.toString();
        case "checkbox":
            return change.newCell.checked ? "true" : "false";
        case "date":
            return change.newCell.date?.toISOString() ?? "";
        case "time":
            return change.newCell.time?.toISOString() ?? "";
        case "dropdown":
            return change.newCell.selectedValue ?? "";
        case "header":
        case "text":
        case "email":
        case "chevron":
            return change.newCell.text;
    }
};
